import * as Yup from 'yup';
import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { AccountType, Category, DocumentStatus, UserRole } from '@synergy/models';
import { BestPriceIcon } from '@synergy/theme';
import { Box, ButtonBase, FormHelperText, Tooltip } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PaloBonusBadge, PaloBonusCondition, PaloColumn, PaloListView } from '@synergy/components-palo';
import { ScrollToFieldError } from '@synergy/components';
import { connect } from 'react-redux';
import { tokens } from '@synergy/theme-palo';
import { unique } from '@synergy/utils';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import urlJoin from 'url-join';

import { AccessControl } from '../../../common/components/access-control/access-control';
import { AccountApi } from '../../../common/api/account-api';
import { AppState } from '../../../store/models/app-state';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../common/components/abstract/form-controlled-step-component';
import { Inquiry } from '../models/inquiry';
import { InquiryCategoryGroup, InquirySupplierSearchBar } from '../components/inquiry-supplier-search-bar';
import {
    InquiryDeliveryRegion,
    coverageOf as coverageOfPositions,
    deliveryRegionsOf,
    startingCategoriesOf,
} from '../utils/inquiry-supplier-groups';
import { InquiryDispatchSlot } from '../palo/inquiry-dispatch-slot';
import { PaloDirtyComparison } from '../../../common/components/palo/palo-dirty-context';
import { PaloMeasure, paloSurplus } from '../../../common/components/palo/palo-measure';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { SUPPLIER_NAME_MAX_WIDTH, SUPPLIER_NAME_MIN_WIDTH } from '../utils/palo-supplier-name';
import { Supplier } from '../../../common/models/supplier';
import { SupplierAccount } from '../models/supplier-account';
import { resetSuppliers } from '../../../common/actions/supplier-actions';
import { updateAccounts } from '../../../common/actions/account-actions';

const c = tokens.color;

/** The tick box and the row menu as the list draws them. */
const SELECT_WIDTH = 46;
const CITY_WIDTH = 120;
const TAGS_WIDTH = 200;
const ROW_ACTIONS_WIDTH = 46;

type SuppliersPartialInquiryType = Omit<Partial<Inquiry>, 'supplierAccounts'> & {
    supplierAccounts: SupplierAccount[];
};

interface InquiryDetailSuppliersProps extends FormControlledStepComponentProps<SuppliersPartialInquiryType> {
    suppliers: Supplier[];
    productCategories: Category[];
    /** Per position, the categories its product is filed under. */
    positions: Category[][];
    deliveryRegions: InquiryDeliveryRegion[];
    requesting: boolean;
    updateAccounts: (searchParams: URLSearchParams) => void;
    resetSuppliers: () => void;
}

interface InquiryDetailSuppliersFormValues {
    supplierAccounts: SupplierAccount[];
}

interface InquiryDetailSuppliersState {
    searchParams: URLSearchParams;
    groups: InquiryCategoryGroup[];
    /** What the filter starts with, once it is known which categories have suppliers. */
    startingCategories?: Category[];
}

/** The supplier's POOL bonus, as the list sends it — without a title of its own. */
const bonusOf = (supplier: Supplier): PaloBonusCondition[] =>
    ((supplier as Supplier & { bonus?: PaloBonusCondition[] }).bonus ?? []).map(condition => ({
        ...condition,
        title: condition.title || condition.description || 'POOL-Bonus',
        description: condition.title ? condition.description : null,
    }));

/** A text that ends in "…" where the column is too narrow, with the whole of it on hover (R-185). */
const Ellipsis = ({ text, muted }: { text: string; muted?: boolean }) => (
    <Tooltip title={text} placement="top-start" enterDelay={500}>
        <Box
            component="span"
            sx={{
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: muted ? c.ink3 : undefined,
            }}
        >
            {text}
        </Box>
    </Tooltip>
);

/** How many of the positions a supplier covers, by the groups the filter holds. */
const coverageOf = (
    supplier: Supplier,
    positions: Category[][],
    groups: InquiryCategoryGroup[]
): { covered: number; of: number } => {
    // The list sends the categories a supplier is filed under, parents included.
    const filed = (supplier as Supplier & { categories?: { id: number }[] }).categories ?? [];

    return coverageOfPositions(
        filed.map(category => category.id),
        positions.map(position => position.map(category => category.id)),
        groups.map(group => group.ids)
    );
};

const positionsKeyOf = (positions: Category[][]): string =>
    positions.map(position => position.map(category => category.id).join('.')).join(',');

/**
 * "Passt zu": a bar of the product groups the supplier carries, and the count.
 * At most six segments stand for the share: one per group ran past the column
 * once an inquiry had nine of them (R-188), and the count says the exact number.
 */
const COVERAGE_SEGMENTS = 6;

const PaloCoverage = ({ covered, of }: { covered: number; of: number }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ display: 'inline-flex', gap: '2px' }}>
            {Array.from({ length: Math.min(of, COVERAGE_SEGMENTS) }, (_, index) => (
                <Box
                    key={index}
                    component="span"
                    sx={{
                        width: 6,
                        height: 10,
                        borderRadius: '1px',
                        backgroundColor:
                            index < Math.round((covered / of) * Math.min(of, COVERAGE_SEGMENTS)) ? c.accent : c.line,
                    }}
                />
            ))}
        </Box>
        <FormattedMessage id="app.inquiries.detail.coverageOf" values={{ covered, of }} />
    </Box>
);

/**
 * Picking the suppliers of an inquiry, on the page that sends to them.
 *
 * The dispatch list stands on top — who gets the inquiry and where each of
 * them stands — and this step offers the search for more below it. A supplier
 * ticked here is handed on at once, so it moves up into the list without a
 * save in between; the two steps share the inquiry's suppliers through the
 * wizard's state rather than each keeping a copy (#2258, #2255).
 */
class InquiryDetailSuppliersBase extends FormControlledStepComponent<
    SuppliersPartialInquiryType,
    InquiryDetailSuppliersFormValues,
    InquiryDetailSuppliersProps,
    InquiryDetailSuppliersState
> {
    override state: InquiryDetailSuppliersState = {
        searchParams: new URLSearchParams({
            'filter[type]': 'supplier',
            sort: 'name',
            order: 'asc',
            page: '1',
            limit: '100',
        }),
        groups: [],
    };

    protected initialValues: InquiryDetailSuppliersFormValues = {
        supplierAccounts: [],
    };

    /** What this step handed on last, to tell its own change from the dispatch list's. */
    private handedOn?: SupplierAccount[];

    /*
     * The supplier stops where it does in the dispatch list above; what it
     * would get beyond that goes to place and tags (R-190).
     */
    private columns = (tableWidth: number): PaloColumn<Supplier>[] => {
        const { groups } = this.state;
        const fixed =
            SELECT_WIDTH + 40 + 90 + (groups.length > 0 ? 110 : 0) + CITY_WIDTH + TAGS_WIDTH + ROW_ACTIONS_WIDTH;
        const extra = paloSurplus(tableWidth, fixed, SUPPLIER_NAME_MAX_WIDTH, 2);

        return [
            {
                key: 'contractPartner',
                type: 'flag',
                width: '40px',
                head: <FormattedMessage id="app.inquiries.detail.contractPartnerShort" />,
                cell: supplier => (supplier.isContractPartner ? <BestPriceIcon style={{ fontSize: 20 }} /> : null),
            },
            {
                key: 'name',
                type: 'name',
                width: tableWidth
                    ? `${Math.max(SUPPLIER_NAME_MIN_WIDTH, Math.min(SUPPLIER_NAME_MAX_WIDTH, tableWidth - fixed))}px`
                    : undefined,
                head: <FormattedMessage id="app.table.column.supplier" />,
                cell: supplier => (
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <Ellipsis text={supplier.name} muted={supplier.isLocked} />
                        {supplier.isLocked && (
                            <Box
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    padding: '0 7px',
                                    fontSize: '11px',
                                    border: `1px solid ${c.line}`,
                                    borderRadius: '999px',
                                    color: c.ink3,
                                }}
                            >
                                <FormattedMessage id="app.inquiries.detail.supplierLocked" />
                            </Box>
                        )}
                    </Box>
                ),
            },
            {
                key: 'bonus',
                type: 'flag',
                width: '90px',
                head: <FormattedMessage id="app.table.column.bonus" />,
                // The same badge as in the offers: the rate at a glance, all
                // conditions on a click (GF 14.10.2026).
                cell: supplier => <PaloBonusBadge conditions={bonusOf(supplier)} supplierName={supplier.name} />,
            },
            ...(groups.length > 0
                ? [
                      {
                          key: 'coverage',
                          type: 'classification' as const,
                          width: '110px',
                          head: <FormattedMessage id="app.inquiries.detail.coverage" />,
                          cell: (supplier: Supplier) => (
                              <PaloCoverage {...coverageOf(supplier, this.props.positions, groups)} />
                          ),
                      },
                  ]
                : []),
            {
                key: 'city',
                type: 'classification',
                width: `${CITY_WIDTH + extra}px`,
                head: <FormattedMessage id="app.inquiries.dispatch.city" />,
                cell: supplier => <Ellipsis text={supplier.getCity() ?? ''} muted={supplier.isLocked} />,
            },
            {
                key: 'tags',
                width: `${TAGS_WIDTH + extra}px`,
                wrap: true,
                head: <FormattedMessage id="app.table.column.tags" />,
                cell: supplier => (
                    <Box component="span" sx={{ color: c.ink3 }}>
                        {supplier.getTags()}
                    </Box>
                ),
            },
        ];
    };

    /** The supplier's own mask, opened beside the request. */
    private supplierActions = (supplier: Supplier) => [
        {
            label: <FormattedMessage id="app.common.edit" />,
            onClick: () =>
                window.open(
                    urlJoin(
                        BASE_URL,
                        ROUTES.contactsAndOrganizations.suppliers.detail.replace(':id', supplier.id.toString())
                    ),
                    '_blank',
                    'noopener,noreferrer'
                ),
        },
    ];

    protected onSubmit = (values: InquiryDetailSuppliersFormValues) => {
        // Existing demandCustomer-Id can get lost by deselecting and reselecting an customer
        // Remap ids to customers which have been selected before submitting
        (this.props.internalState?.supplierAccounts ?? []).forEach(oldSelectedSupplier => {
            const index = values.supplierAccounts.findIndex(
                supplier => supplier.account.id === oldSelectedSupplier.account.id
            );
            if (index >= 0 && undefined === values.supplierAccounts[index].id) {
                values.supplierAccounts[index].id = oldSelectedSupplier.id;
            }
        });
        this.handOn(values.supplierAccounts);
    };

    private handOn(supplierAccounts: SupplierAccount[]): void {
        this.handedOn = supplierAccounts;
        this.props.propagateInternalState({ supplierAccounts });
    }

    private setPicked(supplierAccounts: SupplierAccount[]): void {
        this.formikInstance?.setFieldValue('supplierAccounts', supplierAccounts);
        this.handOn(supplierAccounts);
    }

    private accountApi = new AccountApi(API_BASE_URL);
    private startingFor?: string;

    public componentDidMount() {
        this.props.resetSuppliers();
        this.formikInstance?.setValues({ supplierAccounts: this.props.internalState?.supplierAccounts ?? [] });
        this.findStartingCategories();
    }

    public componentDidUpdate() {
        this.findStartingCategories();
    }

    /** Per position the deepest category some supplier carries (#2326 R-252). */
    private findStartingCategories() {
        const key = positionsKeyOf(this.props.positions);

        if (this.startingFor === key) {
            return;
        }
        this.startingFor = key;

        startingCategoriesOf(this.props.positions, async categoryId => {
            const response = await this.accountApi.cget(
                new URLSearchParams({
                    'filter[type]': 'supplier',
                    'filter[categoryIds]': String(categoryId),
                    limit: '1',
                })
            );
            // Unanswered, the category counts as carried: better too wide a filter than none.
            return !response.success || response.result.total > 0;
        }).then(startingCategories => {
            if (this.startingFor === key) {
                this.setState({ startingCategories });
            }
        });
    }

    public componentWillUnmount() {
        this.props.resetSuppliers();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: InquiryDetailSuppliersProps) {
        if (this.props.internalState !== nextProps.internalState && nextProps.internalState !== this.handedOn) {
            this.formikInstance?.setValues({ supplierAccounts: nextProps.internalState?.supplierAccounts ?? [] });
            return;
        }

        // The dispatch list changes the suppliers too — a contact picked for
        // one, one taken off the list. Taken over here, so a save from this
        // step does not put the old ones back.
        const next = nextProps.globalState?.supplierAccounts;

        if (next && next !== this.props.globalState?.supplierAccounts && next !== this.handedOn) {
            this.formikInstance?.setValues({ supplierAccounts: next });
        }
    }

    private pick = (ids: number[]) => {
        const current = this.formikInstance?.values.supplierAccounts ?? [];
        const added = this.props.suppliers
            .filter(supplier => ids.includes(supplier.id) && !supplier.isLocked)
            .filter(supplier => !current.some(supplierAccount => supplierAccount.account.id === supplier.id))
            .map(supplier => ({ account: supplier, contact: supplier.getMainContact() } as SupplierAccount));

        if (added.length > 0) {
            this.setPicked([...current, ...added]);
        }
    };

    private renderSearch(form: FormikProps<InquiryDetailSuppliersFormValues>) {
        const pickedIds = form.values.supplierAccounts.map(supplierAccount => supplierAccount.account.id);
        const { groups } = this.state;
        const startingCategories = this.state.startingCategories;

        if (!startingCategories) {
            return null;
        }

        // The picked ones stand in the dispatch list above; the search shows
        // the rest, the best match first (#2258).
        const items = this.props.suppliers
            .filter(supplier => !pickedIds.includes(supplier.id))
            .map((supplier, order) => ({
                supplier,
                order,
                coverage: coverageOf(supplier, this.props.positions, groups).covered,
            }))
            .sort(
                (left, right) =>
                    right.coverage - left.coverage ||
                    Number(right.supplier.isContractPartner) - Number(left.supplier.isContractPartner) ||
                    left.order - right.order
            )
            .map(({ supplier }) => supplier);
        const lockedIds = items.filter(supplier => supplier.isLocked).map(supplier => supplier.id);
        const pickable = items.filter(supplier => !supplier.isLocked).map(supplier => supplier.id);

        return (
            <>
                <InquirySupplierSearchBar
                    defaultCategories={startingCategories}
                    knownCategories={this.props.productCategories}
                    deliveryRegions={this.props.deliveryRegions}
                    searchParams={this.state.searchParams}
                    onSearch={this.handleFilterChanged}
                    onGroupsChange={this.handleGroupsChange}
                    action={
                        <AccessControl allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
                            <ButtonBase
                                href={urlJoin(BASE_URL, ROUTES.contactsAndOrganizations.suppliers.new)}
                                target="_blank"
                                sx={{
                                    gap: '4px',
                                    fontSize: '12.5px',
                                    color: c.accentHi,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                <AddIcon sx={{ fontSize: 17 }} />
                                <FormattedMessage id="app.inquiries.detail.newSupplier" />
                            </ButtonBase>
                        </AccessControl>
                    }
                />
                <PaloMeasure>
                    {tableWidth => (
                        <PaloListView<Supplier, number>
                            embedded={true}
                            columns={this.columns(tableWidth)}
                            items={items}
                            getRowId={supplier => supplier.id}
                            requesting={this.props.requesting}
                            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                            rowActions={this.supplierActions}
                            selection={{
                                selectedIds: [],
                                allSelected: false,
                                someSelected: false,
                                // Ticking takes the supplier up into the dispatch list
                                // at once; the head box takes every one shown.
                                onToggle: id => this.pick([id]),
                                onToggleAll: () => this.pick(pickable),
                                disabledIds: lockedIds,
                                disabledHint: <FormattedMessage id="app.inquiries.detail.supplierLockedHint" />,
                            }}
                        />
                    )}
                </PaloMeasure>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '6px 2px 0',
                        fontSize: '11.5px',
                        color: c.ink3,
                    }}
                >
                    <span>
                        <FormattedMessage
                            id={
                                groups.length > 0
                                    ? 'app.inquiries.detail.searchSummarySorted'
                                    : 'app.inquiries.detail.searchSummary'
                            }
                            values={{ count: items.length }}
                        />
                    </span>
                    <span>
                        <FormattedMessage id="app.inquiries.detail.pickedMoveUp" />
                    </span>
                </Box>
            </>
        );
    }

    private handleGroupsChange = (groups: InquiryCategoryGroup[]) => {
        const same =
            groups.length === this.state.groups.length &&
            groups.every((group, index) => group.ids.join() === this.state.groups[index]?.ids.join());

        if (!same) {
            this.setState({ groups });
        }
    };

    public handleFilterChanged = (searchParams: URLSearchParams) => {
        this.setState({ searchParams });
        this.props.updateAccounts(searchParams);
    };

    public render() {
        const { globalState, unlocked } = this.props;

        const readonly =
            (!unlocked &&
                globalState?.status !== DocumentStatus.Created &&
                globalState?.status !== DocumentStatus.Ongoing) ||
            false;

        const validationSchema = Yup.object().shape({
            supplierAccounts: Yup.array().required('app.error.required'),
        });

        return (
            <Formik
                innerRef={ref => (this.formikInstance = ref)}
                initialValues={this.initialValues}
                onSubmit={this.onSubmit}
                validationSchema={validationSchema}
            >
                {(formikBag: FormikProps<InquiryDetailSuppliersFormValues>) => (
                    <Form>
                        <PaloDirtyComparison
                            baseline={{ supplierAccounts: this.props.documentState?.supplierAccounts ?? [] }}
                        />
                        <ScrollToFieldError />
                        {/* Who gets the inquiry, drawn here by its own step. */}
                        <InquiryDispatchSlot />
                        {formikBag.submitCount > 0 && formikBag.errors.supplierAccounts && (
                            <FormHelperText error={true}>
                                <FormattedMessage id={String(formikBag.errors.supplierAccounts)} />
                            </FormHelperText>
                        )}
                        {/* Once the inquiry is through, the list on top says it
                            all; there is nobody left to add. */}
                        {!readonly && <Box sx={{ mt: '18px' }}>{this.renderSearch(formikBag)}</Box>}
                    </Form>
                )}
            </Formik>
        );
    }
}

const mapStateToProps = (state: AppState) => {
    // Get all accountPurchases->purchaseItems->Products->Categories
    const categories =
        state.inquiries.entity?.accountPurchases
            .map(x => x.purchaseItems?.map(y => y.product?.categories ?? []) ?? [])
            .flat(2) ?? [];
    const uniqueCategories = unique(categories, category => category.id);
    const items = unique(
        state.inquiries.entity?.accountPurchases.flatMap(x => x.purchaseItems ?? []).filter(y => y.product) ?? [],
        item => item.product?.id
    );

    return {
        positions: items.map(item => item.product?.categories ?? []),
        deliveryRegions: deliveryRegionsOf(
            state.inquiries.entity?.accountPurchases.map(
                x => x.deliveryAddress as { region?: unknown; zip?: string } | undefined
            ) ?? []
        ),
        // Show only suppliers with contacts
        suppliers: state.accounts.flat.filter(account => account.type === AccountType.Supplier) as Supplier[],
        productCategories: uniqueCategories,
        requesting: state.suppliers.requesting,
    };
};

export const InquiryDetailSuppliers = connect(mapStateToProps, {
    updateAccounts,
    resetSuppliers,
})(InquiryDetailSuppliersBase);
